import { Bitset } from "./bitset.ts";

const N_MONOGRAMS = 27; // 26 letters + one non-letter

const UPPER_A = "A".charCodeAt(0);
const UPPER_Z = "Z".charCodeAt(0);
const LOWER_A = "a".charCodeAt(0);
// "{" is the next ASCII value after "z"
const LEFT_BRACE = "{".charCodeAt(0);

/** Normalizes a single character to the range 0-26.
 *
 * Case is ignored, and all non-letter characters are assigned code 26.
 */
// micro-optimization to do this manually vs. using things like `toLowerCase`.
const normalCode = (code: number) =>
  (code < UPPER_A
    ? LEFT_BRACE
    : code <= UPPER_Z
      ? code + LOWER_A - UPPER_A
      : code < LOWER_A
        ? LEFT_BRACE
        : code < LEFT_BRACE
          ? code
          : LEFT_BRACE) - LOWER_A;

/** Returns all the digrams of a string as an array of digram indices
 *
 * Digrams are normalized by converting all word characters to lower case,
 * and treating all non-word characters as the same character. Digram indices
 * are the index of sorting resulting digrams alphabetically (with non-word
 * character after all word characters). So index 0 represents "aa" and index
 * 728 represents the repeated non-word character.
 */
export const digrams = (input: string) => {
  const padded = input.length < 2 ? input.padEnd(2, " ") : input;
  const out: number[] = Array(padded.length - 1);
  // Optimization: only calculate one code point per string input character
  // This speeds up digram calculation by 10%
  let a = normalCode(padded.charCodeAt(0));
  let b = 0;
  for (let ix = 0; ix < input.length - 1; ix++) {
    b = normalCode(padded.charCodeAt(ix + 1));
    out[ix] = a * N_MONOGRAMS + b;
    a = b;
  }
  return out;
};

export const DigramBitset = (terms: string[] | string) => {
  const bitset = new Bitset({ maxSize: N_MONOGRAMS ** 2 });
  for (const term of typeof terms === "string" ? [terms] : terms) {
    bitset.setAll(digrams(term));
  }
  return bitset;
};
